/**
 * Centralized service for generating media URLs using the hosting infrastructure
 * Provides clean, SEO-friendly URLs that leverage the Firebase Hosting + Cloud Functions setup
 */

export interface MediaUrls {
  /** Clean public GIF URL for sharing (uses hosting setup) */
  publicGifUrl: string
  /** Clean public MP4 URL for sharing (uses hosting setup) */
  publicMp4Url: string
  /** Preview page URL for social media sharing */
  previewPageUrl: string
  /** Display URL for internal app usage (with Firebase Storage tokens) */
  displayUrl: string
}

/**
 * Generates all relevant URLs for a given GIF
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif') - EXPECTED FORMAT after URL repair
 * @returns MediaUrls - Object containing all URL variants
 */
export const getMediaUrls = (gifUrl: string): MediaUrls => {
  // Extract filename from storage path or full URL
  let filename: string

  if (
    gifUrl.includes('storage.googleapis.com') ||
    gifUrl.includes('firebasestorage.googleapis.com')
  ) {
    // LEGACY FALLBACK: This should not happen after URL repair
    console.error(
      '⚠️ MediaUrlService: Found full URL instead of storage path!',
      {
        gifUrl,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        location: window.location.href,
      }
    )

    // Handle full Firebase Storage URLs (legacy fallback)
    // Extract the UUID from the URL path (last segment after the last slash)
    const urlParts = gifUrl.split('/')
    filename = urlParts[urlParts.length - 1]
    // Remove any query parameters
    filename = filename.split('?')[0]
    // Remove .gif extension if present
    filename = filename.replace('.gif', '')
  } else if (gifUrl.startsWith('gifs/') && gifUrl.endsWith('.gif')) {
    // Expected format: Handle storage path format (e.g., 'gifs/abc123.gif' -> 'abc123')
    filename = gifUrl.replace('gifs/', '').replace('.gif', '')
  } else {
    // UNEXPECTED FORMAT: Log for investigation
    console.error('❌ MediaUrlService: Unexpected URL format!', {
      gifUrl,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      location: window.location.href,
    })

    // Attempt to extract filename as fallback
    filename = gifUrl.replace('gifs/', '').replace('.gif', '')
  }

  // Check if we're in emulator mode
  const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'

  if (isEmulator) {
    // For emulator: use direct Storage emulator URLs with proper REST API format
    const storageBaseUrl = 'http://localhost:9199'
    const hostingBaseUrl = 'http://localhost:5002'
    const projectId =
      import.meta.env.VITE_FIREBASE_PROJECT_ID || 'reaction-gif-app'
    const storageBucket =
      import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || `${projectId}.appspot.com`

    return {
      publicGifUrl: `${storageBaseUrl}/v0/b/${storageBucket}/o/gifs%2F${filename}.gif?alt=media`,
      publicMp4Url: `${storageBaseUrl}/v0/b/${storageBucket}/o/videos%2F${filename}.mp4?alt=media`,
      previewPageUrl: `${hostingBaseUrl}/gif/${filename}`,
      displayUrl: `${storageBaseUrl}/v0/b/${storageBucket}/o/gifs%2F${filename}.gif?alt=media`,
    }
  } else {
    // For production: use clean hosting URLs for sharing, direct storage for display
    const baseUrl = 'https://cyranogifs.com'
    return {
      publicGifUrl: `${baseUrl}/gif/${filename}.gif`,
      publicMp4Url: `${baseUrl}/video/${filename}.mp4`,
      previewPageUrl: `${baseUrl}/gif/${filename}`,
      displayUrl: `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || `${import.meta.env.VITE_FIREBASE_PROJECT_ID || 'reaction-gif-app'}.firebasestorage.app`}/o/gifs%2F${filename}.gif?alt=media`, // Direct storage for fast loading
    }
  }
}

/**
 * Generates a clean public GIF URL for sharing
 * Perfect for reaction GIFs shared in messaging apps
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @returns string - Clean public URL with proper caching and range support for auto-play
 */
export const getPublicGifUrl = (gifUrl: string): string => {
  return getMediaUrls(gifUrl).publicGifUrl
}

/**
 * Generates a clean public MP4 URL for sharing
 * Ideal for platforms that prefer MP4 over GIF
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @returns string - Clean MP4 URL for video sharing
 */
export const getPublicMp4Url = (gifUrl: string): string => {
  return getMediaUrls(gifUrl).publicMp4Url
}

/**
 * Generates a preview page URL for social media sharing
 * Creates rich link previews with Open Graph tags
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @returns string - Preview page URL for social sharing
 */
export const getPreviewPageUrl = (gifUrl: string): string => {
  return getMediaUrls(gifUrl).previewPageUrl
}

/**
 * Generates the best URL for displaying media in the app
 * Uses hosting infrastructure for better performance and caching
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @returns string - Optimized display URL
 */
export const getDisplayUrl = (gifUrl: string): string => {
  return getMediaUrls(gifUrl).displayUrl
}

/**
 * Extracts the GIF ID from a storage path
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif') - EXPECTED FORMAT after URL repair
 * @returns string - The GIF ID (filename without extension)
 */
export const extractGifId = (gifUrl: string): string => {
  if (
    gifUrl.includes('storage.googleapis.com') ||
    gifUrl.includes('firebasestorage.googleapis.com')
  ) {
    // LEGACY FALLBACK: This should not happen after URL repair
    console.error('⚠️ ExtractGifId: Found full URL instead of storage path!', {
      gifUrl,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack,
    })

    // Handle full Firebase Storage URLs (legacy fallback)
    // Extract the UUID from the URL path (last segment after the last slash)
    const urlParts = gifUrl.split('/')
    let filename = urlParts[urlParts.length - 1]
    // Remove any query parameters
    filename = filename.split('?')[0]
    // Remove .gif extension if present
    return filename.replace('.gif', '')
  } else if (gifUrl.startsWith('gifs/') && gifUrl.endsWith('.gif')) {
    // Expected format: Handle storage path format (e.g., 'gifs/abc123.gif' -> 'abc123')
    return gifUrl.replace('gifs/', '').replace('.gif', '')
  } else {
    // UNEXPECTED FORMAT: Log for investigation
    console.error('❌ ExtractGifId: Unexpected URL format!', {
      gifUrl,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack,
    })

    // Attempt to extract filename as fallback
    return gifUrl.replace('gifs/', '').replace('.gif', '')
  }
}

/**
 * Creates a storage path from a GIF ID
 * @param gifId - The GIF ID
 * @returns string - The storage path (e.g., 'gifs/abc123.gif')
 */
export const createStoragePath = (gifId: string): string => {
  return `gifs/${gifId}.gif`
}

/**
 * Checks if a given URL is using the new hosting infrastructure
 * @param url - URL to check
 * @returns boolean - True if using hosting infrastructure
 */
export const isHostingUrl = (url: string): boolean => {
  return url.includes('cyranogifs.com') || url.includes('localhost:4000')
}
